/**
 * Academy progress: what the player has passed, how hard they leaned on the help drawer, and the
 * ratings they have earned. Kept in localStorage as one JSON value, so syncing it to the account
 * service later is a single `PUT /api/progress` with no reshaping.
 */
export const PROGRESS_KEY_V1 = 'atc.academy.v1';
export const PROGRESS_KEY = 'atc.academy.v2';

/** How far down the help drawer the player went: 0 none, 1 hint, 2 say it, 3 show me. */
export type AssistLevel = 0 | 1 | 2 | 3;

export interface LessonRecord {
  /** Completions. 0 while the lesson has only been started. */
  passes: number;
  attempts: number;
  /** The lowest assistance any passing run needed, so improving a result is recorded. */
  assist: AssistLevel;
  /** ISO, set on the first pass. */
  firstPassedAt?: string;
  /** ISO. */
  lastPlayedAt: string;
}

export interface AcademyProgress {
  v: 2;
  lessons: Record<string, LessonRecord>;
  /** Ratings already awarded. Kept so a later change to the lesson list never revokes one. */
  badges: string[];
  updatedAt: string;
}

type Store = Pick<Storage, 'getItem'>;
type WritableStore = Pick<Storage, 'getItem' | 'setItem'>;

const now = () => new Date().toISOString();
export const emptyProgress = (): AcademyProgress => ({ v: 2, lessons: {}, badges: [], updatedAt: now() });

function parse(raw: string | null | undefined): unknown {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

const isRecord = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const count = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? Math.floor(x) : 0);
const assistOf = (x: unknown): AssistLevel => (x === 0 || x === 1 || x === 2 || x === 3 ? x : 3);

/** Anything stored by a newer or corrupted build is coerced rather than thrown away wholesale. */
function normalize(raw: Record<string, unknown>): AcademyProgress {
  const lessons: Record<string, LessonRecord> = {};
  if (isRecord(raw.lessons)) {
    for (const [id, value] of Object.entries(raw.lessons)) {
      if (!isRecord(value)) continue;
      lessons[id] = {
        passes: count(value.passes),
        attempts: Math.max(count(value.attempts), count(value.passes)),
        assist: assistOf(value.assist),
        firstPassedAt: typeof value.firstPassedAt === 'string' ? value.firstPassedAt : undefined,
        lastPlayedAt: typeof value.lastPlayedAt === 'string' ? value.lastPlayedAt : new Date(0).toISOString(),
      };
    }
  }
  const badges = Array.isArray(raw.badges) ? raw.badges.filter((b): b is string => typeof b === 'string') : [];
  return { v: 2, lessons, badges, updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : now() };
}

/**
 * The v1 record was a bare array of completed ids with no attempt or assistance history. An
 * unknown run is recorded as fully assisted: the conservative reading for a rating that later
 * distinguishes unaided work.
 */
function migrate(raw: unknown): AcademyProgress {
  const ids = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
  const at = new Date(0).toISOString();
  return {
    v: 2,
    lessons: Object.fromEntries(ids.map((id) => [id, { passes: 1, attempts: 1, assist: 3 as AssistLevel, firstPassedAt: at, lastPlayedAt: at }])),
    badges: [],
    updatedAt: now(),
  };
}

/** Total: a blocked, empty or corrupted store reads as no progress rather than throwing. */
export function readProgress(storage: Store | undefined): AcademyProgress {
  try {
    const v2 = parse(storage?.getItem(PROGRESS_KEY));
    if (isRecord(v2) && v2.v === 2) return normalize(v2);
    // v1 is left in place for one release, so rolling the build back does not lose the record.
    return migrate(parse(storage?.getItem(PROGRESS_KEY_V1)));
  } catch {
    return emptyProgress();
  }
}

export function writeProgress(storage: WritableStore | undefined, progress: AcademyProgress): void {
  try { storage?.setItem(PROGRESS_KEY, JSON.stringify({ ...progress, updatedAt: now() })); } catch { /* blocked storage: progress lasts for this page only */ }
}

function update(storage: WritableStore | undefined, id: string, change: (r: LessonRecord) => LessonRecord): AcademyProgress {
  const progress = readProgress(storage);
  const current = progress.lessons[id] ?? { passes: 0, attempts: 0, assist: 0 as AssistLevel, lastPlayedAt: now() };
  const next: AcademyProgress = { ...progress, lessons: { ...progress.lessons, [id]: change(current) } };
  writeProgress(storage, next);
  return next;
}

/** Called when a lesson run starts, so a lesson restarted many times is visible later. */
export function markAttempt(storage: WritableStore | undefined, id: string): AcademyProgress {
  return update(storage, id, (r) => ({ ...r, attempts: r.attempts + 1, lastPlayedAt: now() }));
}

export function markCompleted(storage: WritableStore | undefined, id: string, assist: AssistLevel = 3): AcademyProgress {
  return update(storage, id, (r) => ({
    ...r,
    passes: r.passes + 1,
    attempts: Math.max(r.attempts, r.passes + 1),
    assist: r.passes ? (Math.min(r.assist, assist) as AssistLevel) : assist,
    firstPassedAt: r.firstPassedAt ?? now(),
    lastPlayedAt: now(),
  }));
}

export const isPassed = (progress: AcademyProgress, id: string): boolean => (progress.lessons[id]?.passes ?? 0) > 0;

/** Ids the player has passed. Derived, so callers that only need a set stay unchanged. */
export function completedLessons(storage: Store | undefined): Set<string> {
  const progress = readProgress(storage);
  return new Set(Object.keys(progress.lessons).filter((id) => isPassed(progress, id)));
}

/** Passes among a given list, so practice drills never inflate the lesson count. */
export const completedCount = (progress: AcademyProgress, lessons: readonly { id: string }[]): number =>
  lessons.filter((l) => isPassed(progress, l.id)).length;
